import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  ListFilter,
  Loader2,
  Mail,
  Search,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { CDMO_STATUS } from '../constants';
import {
  CLAIM_CAMPAIGN_STATUS_FILTER_LABELS,
  type ClaimCampaignStatusFilter,
} from '../constants/claimEmailCampaign';
import type { CDMOApplication } from '../lib/supabase';
import { dbService } from '../lib/supabase';

/** Server-backed pagination for the Campaign page (reduces payload vs loading all CDMOs). */
export interface ClaimCampaignServerMode {
  totalEligibleCount: number;
  page: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  searchQuery: string;
  onSearchQueryChange: (value: string) => void;
  campaignStatusFilter: ClaimCampaignStatusFilter;
  onCampaignStatusFilterChange: (value: ClaimCampaignStatusFilter) => void;
  /** Debounced search used for the table query; keeps cross-page select aligned with filters. */
  appliedSearchQuery: string;
  isLoadingRows?: boolean;
}

export interface ClaimEmailCampaignPanelProps {
  /** Full CDMO list from matchmaking (same source as cards). */
  cdmoData: CDMOApplication[];
  /** Reload CDMO rows after a campaign start so `campaign_active` updates in the UI. */
  onRefresh: () => Promise<void>;
  /** Use full-page layout (taller table, no bottom margin on the card). */
  fullPage?: boolean;
  /** Render inside unified Campaign page (no outer card; parent supplies shell). */
  embedded?: boolean;
  /**
   * When set, `cdmoData` is one server page and search is controlled by the parent (debounced fetch).
   * When omitted, the panel filters `cdmoData` client-side (legacy).
   */
  serverMode?: ClaimCampaignServerMode;
}

/**
 * Admin-only panel to start the 3-step claim email campaign for unclaimed CDMO profiles.
 */
const ClaimEmailCampaignPanel: React.FC<ClaimEmailCampaignPanelProps> = ({
  cdmoData,
  onRefresh,
  fullPage = false,
  embedded = false,
  serverMode,
}) => {
  const expandedLayout = fullPage || embedded;
  const standaloneCard = fullPage && !embedded;
  const [selected, setSelected] = useState<Set<string>>(new Set());
  /** True while the Edge Function sends campaign emails; UI stays interactive except duplicate Start. */
  const [campaignStartInProgress, setCampaignStartInProgress] = useState(false);
  const [campaignStartProfileCount, setCampaignStartProfileCount] = useState(0);
  const [selectAllAcrossPagesLoading, setSelectAllAcrossPagesLoading] = useState(false);
  const selectPageHeaderCheckboxRef = useRef<HTMLInputElement>(null);
  const [localSearchQuery, setLocalSearchQuery] = useState('');
  const [localCampaignFilter, setLocalCampaignFilter] = useState<ClaimCampaignStatusFilter>('all');

  const searchQuery = serverMode ? serverMode.searchQuery : localSearchQuery;
  const setSearchQuery = serverMode ? serverMode.onSearchQueryChange : setLocalSearchQuery;
  const campaignStatusFilter: ClaimCampaignStatusFilter = serverMode
    ? serverMode.campaignStatusFilter
    : localCampaignFilter;
  const setCampaignStatusFilter = serverMode
    ? serverMode.onCampaignStatusFilterChange
    : setLocalCampaignFilter;

  const eligible = useMemo(() => {
    if (serverMode) {
      return cdmoData.filter((c) => !!c.id);
    }
    return cdmoData.filter((c) => {
      if (!c.id) return false;
      if (c.status !== CDMO_STATUS.UNCLAIMED) return false;
      if (c.user_id) return false;
      if (!(c.claim_token || c.admin_claim_token)) return false;
      return true;
    });
  }, [cdmoData, serverMode]);

  const filteredEligible = useMemo(() => {
    if (serverMode) {
      return eligible;
    }
    let rows = eligible;
    const q = searchQuery.trim().toLowerCase();
    if (q) {
      rows = rows.filter((c) => {
        const company = (c.company_name || '').toLowerCase();
        const mail = (c.primary_email || c.email || '').toLowerCase();
        return company.includes(q) || mail.includes(q);
      });
    }
    if (campaignStatusFilter === 'active') {
      rows = rows.filter((c) => !!c.campaign_active);
    } else if (campaignStatusFilter === 'idle') {
      rows = rows.filter((c) => !c.campaign_active);
    }
    return rows;
  }, [eligible, searchQuery, serverMode, campaignStatusFilter]);

  const pageSelectableRows = useMemo(
    () => filteredEligible.filter((c) => c.id && !c.campaign_active),
    [filteredEligible]
  );
  const pageSelectableIds = useMemo(
    () => pageSelectableRows.map((c) => c.id as string),
    [pageSelectableRows]
  );
  const allPageSelectableSelected =
    pageSelectableIds.length > 0 && pageSelectableIds.every((id) => selected.has(id));
  const somePageSelectableSelected =
    pageSelectableIds.some((id) => selected.has(id)) && !allPageSelectableSelected;

  useEffect(() => {
    const el = selectPageHeaderCheckboxRef.current;
    if (el) {
      el.indeterminate = somePageSelectableSelected;
    }
  }, [somePageSelectableSelected, allPageSelectableSelected, pageSelectableIds.length]);

  const toggle = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectAllEligibleNotActive = async () => {
    if (serverMode) {
      setSelectAllAcrossPagesLoading(true);
      try {
        const searchForQuery =
          serverMode.appliedSearchQuery ?? serverMode.searchQuery;
        const ids = await dbService.getClaimCampaignSelectableCdmoIds({
          search: searchForQuery,
          campaignStatus: serverMode.campaignStatusFilter,
        });
        setSelected(new Set(ids));
        if (ids.length === 0) {
          toast.error('No profiles match this filter without an active campaign.');
        } else {
          toast.success(`Selected ${ids.length} profile${ids.length !== 1 ? 's' : ''} (all pages).`);
        }
      } catch (e) {
        console.error(e);
        toast.error('Could not load profiles to select.');
      } finally {
        setSelectAllAcrossPagesLoading(false);
      }
      return;
    }
    const ids = filteredEligible.filter((c) => !c.campaign_active).map((c) => c.id as string);
    setSelected(new Set(ids));
    if (ids.length === 0) {
      toast.error('No profiles match without an active campaign.');
    }
  };

  const toggleSelectAllOnPage = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (allPageSelectableSelected) {
        pageSelectableIds.forEach((id) => next.delete(id));
      } else {
        pageSelectableIds.forEach((id) => next.add(id));
      }
      return next;
    });
  };

  const startForSelected = async () => {
    const ids = [...selected].filter((id) => {
      const row = cdmoData.find((c) => c.id === id);
      if (!row) {
        // ID is from a page not currently loaded — cannot check client-side.
        // The Edge Function skips CDMOs where campaign_active = true, so server-side
        // idempotency is the safety net for cross-page selections.
        return true;
      }
      return !row.campaign_active;
    });
    if (ids.length === 0) {
      toast.error('Select at least one CDMO that is not already in an active campaign.');
      return;
    }
    setCampaignStartInProgress(true);
    setCampaignStartProfileCount(ids.length);
    try {
      const res = await dbService.startClaimEmailCampaign({ cdmoIds: ids });
      const ok = res.results.filter((r) => r.ok).length;
      const fail = res.results.length - ok;
      toast.success(`Campaign started for ${ok} profile(s).${fail ? ` ${fail} skipped.` : ''}`);
      await onRefresh();
      setSelected(new Set());
    } catch (e) {
      console.error(e);
      toast.error('Failed to start campaign.');
    } finally {
      setCampaignStartInProgress(false);
      setCampaignStartProfileCount(0);
    }
  };

  // DEV / SAFETY: bulk “send to all unclaimed” disabled — uncomment when ready for production.
  // const startAllUnclaimed = async () => {
  //   setLoading(true);
  //   try {
  //     const res = await dbService.startClaimEmailCampaign({ allUnclaimed: true });
  //     const ok = res.results.filter((r) => r.ok).length;
  //     toast.success(`Campaign started for ${ok} unclaimed profile(s) with claim links.`);
  //     await onRefresh();
  //     setSelected(new Set());
  //   } catch (e) {
  //     console.error(e);
  //     toast.error('Failed to start campaign for all unclaimed.');
  //   } finally {
  //     setLoading(false);
  //   }
  // };

  const totalPages =
    serverMode && serverMode.pageSize > 0
      ? Math.max(1, Math.ceil(serverMode.totalEligibleCount / serverMode.pageSize))
      : 1;
  const rangeStart =
    serverMode && serverMode.totalEligibleCount > 0
      ? (serverMode.page - 1) * serverMode.pageSize + 1
      : 0;
  const rangeEnd =
    serverMode && serverMode.totalEligibleCount > 0
      ? filteredEligible.length > 0
        ? rangeStart + filteredEligible.length - 1
        : Math.min(serverMode.page * serverMode.pageSize, serverMode.totalEligibleCount)
      : 0;

  const renderPagination = () => {
    if (!serverMode || totalPages <= 1) return null;

    const currentPage = serverMode.page;
    const getPageNumbers = () => {
      const pages: number[] = [];
      const maxVisible = 5;
      if (totalPages <= maxVisible) {
        for (let i = 1; i <= totalPages; i++) pages.push(i);
      } else {
        const start = Math.max(1, currentPage - 2);
        const end = Math.min(totalPages, start + maxVisible - 1);
        for (let i = start; i <= end; i++) pages.push(i);
      }
      return pages;
    };

    return (
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mt-4 pt-4 border-t border-charcoal/10">
        {!serverMode.isLoadingRows && rangeStart > 0 && (
          <p className="text-sm text-slate-gray">
            Showing {rangeStart}–{rangeEnd} of {serverMode.totalEligibleCount} profile
            {serverMode.totalEligibleCount !== 1 ? 's' : ''}
          </p>
        )}
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => serverMode.onPageChange(Math.max(1, currentPage - 1))}
            disabled={currentPage === 1 || !!serverMode.isLoadingRows}
            className="p-2 rounded-lg border border-slate-gray/20 text-slate-gray hover:bg-slate-gray/10 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            aria-label="Previous page"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <div className="flex items-center gap-1">
            {getPageNumbers().map((p) => (
              <button
                key={p}
                type="button"
                onClick={() => serverMode.onPageChange(p)}
                disabled={!!serverMode.isLoadingRows}
                className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-50 ${
                  currentPage === p
                    ? 'bg-electric-blue text-white'
                    : 'text-slate-gray hover:bg-slate-gray/10'
                }`}
              >
                {p}
              </button>
            ))}
          </div>
          <button
            type="button"
            onClick={() => serverMode.onPageChange(Math.min(totalPages, currentPage + 1))}
            disabled={currentPage === totalPages || !!serverMode.isLoadingRows}
            className="p-2 rounded-lg border border-slate-gray/20 text-slate-gray hover:bg-slate-gray/10 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            aria-label="Next page"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    );
  };

  /**
   * Indeterminate progress while outbound campaign emails are sent; does not block the rest of the panel.
   */
  const renderCampaignStartProgress = () => {
    if (!campaignStartInProgress) return null;
    return (
      <div
        role="status"
        aria-live="polite"
        aria-busy="true"
        className="flex items-start gap-2.5 shrink-0 rounded-xl border border-electric-blue/30 bg-electric-blue/[0.06] px-3 py-2.5 shadow-sm sm:max-w-xs"
      >
        <Loader2
          className="w-5 h-5 text-electric-blue animate-spin shrink-0 mt-0.5"
          aria-hidden
        />
        <div className="min-w-0 text-left">
          <p className="text-sm font-semibold text-charcoal">Sending campaign emails</p>
          <p className="text-xs text-slate-gray mt-0.5 leading-snug">
            Processing {campaignStartProfileCount} profile{campaignStartProfileCount !== 1 ? 's' : ''}. You can keep
            searching and browsing.
          </p>
        </div>
      </div>
    );
  };

  const renderCampaignActionButtons = () => (
    <div className="flex flex-wrap gap-2 shrink-0">
      <button
        type="button"
        onClick={() => void selectAllEligibleNotActive()}
        disabled={selectAllAcrossPagesLoading || campaignStartInProgress}
        className="px-3 py-2 text-sm font-medium rounded-xl border border-charcoal/20 text-charcoal hover:bg-charcoal/5 disabled:opacity-50 inline-flex items-center gap-2"
      >
        {selectAllAcrossPagesLoading ? (
          <Loader2 className="w-4 h-4 animate-spin shrink-0" aria-hidden />
        ) : null}
        Select unclaimed (no active campaign)
      </button>
      <button
        type="button"
        disabled
        title="Bulk send to all unclaimed CDMOs is disabled. Use individual selection and Start campaign."
        aria-disabled="true"
        className="px-3 py-2 text-sm font-medium rounded-xl border border-slate-gray/25 text-slate-gray opacity-60 cursor-not-allowed"
      >
        Send to all unclaimed
      </button>
      <button
        type="button"
        onClick={startForSelected}
        disabled={campaignStartInProgress}
        className="px-4 py-2 text-sm font-semibold rounded-xl bg-gradient-primary text-white hover:shadow-md disabled:opacity-50 inline-flex items-center gap-2"
      >
        {campaignStartInProgress ? <Loader2 className="w-4 h-4 animate-spin" aria-hidden /> : null}
        Start campaign
      </button>
    </div>
  );

  /** Server: compact empty only when nothing to show and no search/status narrowing. */
  const hasNarrowingFilter =
    searchQuery.trim().length > 0 || campaignStatusFilter !== 'all';
  const showEmptyEligible =
    serverMode && !serverMode.isLoadingRows
      ? serverMode.totalEligibleCount === 0 && !hasNarrowingFilter
      : eligible.length === 0;

  if (showEmptyEligible) {
    if (expandedLayout) {
      const emptyWrapperClass = standaloneCard
        ? 'rounded-2xl border border-electric-blue/20 bg-white shadow-sm flex flex-col flex-1 min-h-0 p-6 sm:p-8'
        : 'flex flex-col flex-1 min-h-0';
      return (
        <div className={emptyWrapperClass}>
          <header className="mb-5 pb-5 border-b border-charcoal/10 shrink-0">
            <h2 className="text-lg font-bold text-charcoal flex items-center gap-2">
              <Mail className="w-5 h-5 text-electric-blue shrink-0" aria-hidden />
              CDMO claim campaign
            </h2>
            <p className="mt-2 w-full text-sm text-slate-gray leading-relaxed max-w-3xl">
              Send claim invitations to unclaimed CDMO profiles. The first message goes out as soon as you start a
              campaign; if a profile stays unclaimed, follow-up reminders are sent automatically. Only one campaign
              runs per profile at a time. When someone claims a profile, the campaign stops and admins receive the
              usual notification.
            </p>
          </header>
          <p className="text-sm text-slate-gray">
            No unclaimed CDMO profiles with claim links are available right now.
          </p>
        </div>
      );
    }
    return (
      <div
        className={`rounded-2xl border border-dashed border-slate-gray/40 bg-white/70 p-4 text-sm text-slate-gray ${
          expandedLayout ? '' : 'mb-6'
        }`}
      >
        <p className="font-medium text-charcoal">Claim email campaign</p>
        <p className="mt-1">No unclaimed CDMO profiles with claim tokens are available.</p>
      </div>
    );
  }

  const wrapperClass = embedded
    ? 'flex flex-col flex-1 min-h-0'
    : standaloneCard
      ? 'rounded-2xl border border-electric-blue/20 bg-white shadow-sm flex flex-col min-h-0 flex-1 mb-0 p-6 sm:p-8'
      : 'rounded-2xl border border-electric-blue/25 bg-white/95 backdrop-blur-sm flex flex-col min-h-0 mb-8 p-4 sm:p-5';

  return (
    <div className={wrapperClass}>
      {expandedLayout ? (
        <header className="mb-5 pb-5 border-b border-charcoal/10 shrink-0">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between lg:gap-6">
            <div className="min-w-0 flex-1">
              <h2 className="text-lg font-bold text-charcoal flex items-center gap-2">
                <Mail className="w-5 h-5 text-electric-blue shrink-0" aria-hidden />
                CDMO claim campaign
              </h2>
              <p className="mt-2 w-full text-sm text-slate-gray leading-relaxed max-w-3xl">
                Send claim invitations to unclaimed CDMO profiles. The first message goes out as soon as you start a
                campaign; if a profile stays unclaimed, follow-up reminders are sent automatically. Only one campaign
                runs per profile at a time. When someone claims a profile, the campaign stops and admins receive the
                usual notification.
              </p>
            </div>
            <div className="flex w-full justify-end lg:ml-auto lg:w-auto lg:shrink-0 lg:pt-1">
              {renderCampaignStartProgress()}
            </div>
          </div>
        </header>
      ) : (
        <div className="flex flex-col gap-3 mb-4">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
            <div className="min-w-0 flex-1">
              <h2 className="text-lg font-bold text-charcoal flex items-center gap-2">
                <Mail className="w-5 h-5 text-electric-blue" aria-hidden />
                Claim email campaign
              </h2>
              <p className="text-sm text-slate-gray mt-1 max-w-2xl">
                Send invitations to unclaimed profiles. The first email goes out when you start a campaign; further
                reminders are sent automatically while a profile remains unclaimed. One active campaign per profile;
                claiming ends the campaign and triggers the standard admin notification.
              </p>
            </div>
            <div className="flex flex-col items-stretch sm:items-end gap-2 shrink-0">
              {renderCampaignStartProgress()}
              {renderCampaignActionButtons()}
            </div>
          </div>
        </div>
      )}

      {expandedLayout ? <div className="mb-5">{renderCampaignActionButtons()}</div> : null}

      <div className="mb-3 flex flex-col sm:flex-row sm:flex-wrap sm:items-end gap-3">
        <div className="w-full max-w-md">
          <label htmlFor="claim-campaign-cdmo-search" className="sr-only">
            Search CDMOs by company or email
          </label>
          <div className="relative">
            <Search
              className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-gray pointer-events-none"
              aria-hidden
            />
            <input
              id="claim-campaign-cdmo-search"
              type="search"
              placeholder="Search by company or email…"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-3 py-2.5 text-sm rounded-xl border border-charcoal/15 bg-white text-charcoal placeholder:text-slate-gray/70 shadow-sm focus:outline-none focus:ring-2 focus:ring-electric-blue/30 focus:border-electric-blue/40"
            />
          </div>
        </div>
        <div className="w-full sm:w-auto sm:min-w-[11.5rem]">
          <label
            htmlFor="claim-campaign-status-filter"
            className="mb-1.5 flex items-center gap-1.5 text-xs font-medium text-slate-gray"
          >
            <ListFilter className="w-3.5 h-3.5 shrink-0" aria-hidden />
            Campaign status
          </label>
          <select
            id="claim-campaign-status-filter"
            value={campaignStatusFilter}
            onChange={(e) =>
              setCampaignStatusFilter(e.target.value as ClaimCampaignStatusFilter)
            }
            className="w-full sm:w-[200px] px-3 py-2.5 text-sm rounded-xl border border-charcoal/15 bg-white text-charcoal shadow-sm focus:outline-none focus:ring-2 focus:ring-electric-blue/30 focus:border-electric-blue/40"
          >
            {(Object.keys(CLAIM_CAMPAIGN_STATUS_FILTER_LABELS) as ClaimCampaignStatusFilter[]).map(
              (key) => (
                <option key={key} value={key}>
                  {CLAIM_CAMPAIGN_STATUS_FILTER_LABELS[key]}
                </option>
              )
            )}
          </select>
        </div>
        {!serverMode && (searchQuery.trim() || campaignStatusFilter !== 'all') ? (
          <p className="w-full text-xs text-slate-gray sm:ml-auto sm:w-auto sm:self-end">
            Showing {filteredEligible.length} of {eligible.length} profile{eligible.length !== 1 ? 's' : ''}
          </p>
        ) : null}
        {serverMode && (searchQuery.trim() || campaignStatusFilter !== 'all') ? (
          <p className="w-full text-xs text-slate-gray sm:ml-auto sm:w-auto sm:self-end">
            Filters apply to all eligible profiles; counts update after results load.
          </p>
        ) : null}
      </div>

      <div
        className={`overflow-auto rounded-xl border border-charcoal/10 min-h-0 relative ${
          expandedLayout ? 'flex-1 max-h-[min(70vh,900px)] sm:max-h-[calc(100vh-13rem)]' : 'max-h-56'
        }`}
      >
        {serverMode?.isLoadingRows ? (
          <div
            className="absolute inset-0 z-10 flex items-center justify-center bg-white/70 backdrop-blur-[1px] rounded-xl"
            aria-busy="true"
            aria-live="polite"
          >
            <Loader2 className="w-8 h-8 text-electric-blue animate-spin" aria-hidden />
            <span className="sr-only">Loading profiles</span>
          </div>
        ) : null}
        <table className="w-full text-sm border-collapse">
          <thead className="text-left text-xs uppercase tracking-wide text-slate-gray">
            <tr>
              <th
                scope="col"
                className="sticky top-0 z-20 w-12 min-w-[2.75rem] p-2 bg-white border-b border-charcoal/15 shadow-[0_1px_0_0_rgba(15,23,42,0.06)]"
              >
                <input
                  ref={selectPageHeaderCheckboxRef}
                  type="checkbox"
                  className="rounded border-charcoal/30"
                  checked={allPageSelectableSelected}
                  onChange={toggleSelectAllOnPage}
                  disabled={
                    pageSelectableIds.length === 0 ||
                    campaignStartInProgress ||
                    selectAllAcrossPagesLoading
                  }
                  aria-label="Select all profiles on this page without an active campaign"
                />
              </th>
              <th
                scope="col"
                className="sticky top-0 z-20 p-2 bg-white border-b border-charcoal/15 shadow-[0_1px_0_0_rgba(15,23,42,0.06)]"
              >
                Company
              </th>
              <th
                scope="col"
                className="sticky top-0 z-20 p-2 bg-white border-b border-charcoal/15 shadow-[0_1px_0_0_rgba(15,23,42,0.06)] hidden sm:table-cell"
              >
                Email
              </th>
              <th
                scope="col"
                className="sticky top-0 z-20 p-2 bg-white border-b border-charcoal/15 shadow-[0_1px_0_0_rgba(15,23,42,0.06)]"
              >
                Campaign
              </th>
            </tr>
          </thead>
          <tbody className="bg-white">
            {filteredEligible.length === 0 ? (
              <tr>
                <td colSpan={4} className="p-8 text-center text-sm text-slate-gray">
                  No profiles match your search or status filter. Try adjusting the filters or search terms.
                </td>
              </tr>
            ) : null}
            {filteredEligible.map((c) => {
              const id = c.id as string;
              const active = !!c.campaign_active;
              const stage = c.claim_email_stage ?? 0;
              return (
                <tr key={id} className="border-t border-charcoal/10 hover:bg-charcoal/[0.02]">
                  <td className="p-2 align-middle">
                    {active ? (
                      <span
                        className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-lime-green/15"
                        title="Invitation sent — row locked until campaign ends"
                        aria-label={`Invitation sent for ${c.company_name}. Cannot select while campaign is active.`}
                      >
                        <CheckCircle2
                          className="w-5 h-5 text-lime-green shrink-0"
                          aria-hidden
                          strokeWidth={2.25}
                        />
                      </span>
                    ) : (
                      <input
                        type="checkbox"
                        className="rounded border-charcoal/30"
                        checked={selected.has(id)}
                        onChange={() => toggle(id)}
                        aria-label={`Select ${c.company_name}`}
                      />
                    )}
                  </td>
                  <td className="p-2 font-medium text-charcoal">{c.company_name}</td>
                  <td className="p-2 text-slate-gray hidden sm:table-cell">
                    {c.primary_email || c.email || '—'}
                  </td>
                  <td className="p-2">
                    {active ? (
                      <span className="text-xs font-medium text-green-800 bg-lime-green/10 border border-lime-green/25 px-2 py-1 rounded-md">
                        Invitation sent · stage {stage}
                      </span>
                    ) : (
                      <span className="text-xs text-slate-gray">Idle</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {renderPagination()}
    </div>
  );
};

export default ClaimEmailCampaignPanel;
